import React, { useEffect, useRef, useState } from "react";
import {
    Pencil,
    Highlighter,
    Type,
    ArrowRight,
    Undo,
    Save,
    FilePlus2,
    X,
} from "lucide-react";

/**
 * Lightweight image annotation modal.
 * Supports: freehand pen, highlight rectangles, arrows, and text.
 * Produces a flattened PNG blob on save.
 */
const ImageAnnotatorModal = ({ imageFile, onClose, onSave }) => {
    const [imageUrl, setImageUrl] = useState(null);
    const baseCanvasRef = useRef(null);
    const overlayCanvasRef = useRef(null);
    const containerRef = useRef(null);
    const textInputRef = useRef(null);
    const ignoreBlurRef = useRef(false);

    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [tool, setTool] = useState("pen"); // pen | highlight | arrow | text
    const [strokeColor, setStrokeColor] = useState("#ff3b30");
    const [strokeWidth, setStrokeWidth] = useState(4);
    const [highlightColor, setHighlightColor] = useState("#fff59d");

    const [actions, setActions] = useState([]);
    const [redoStack, setRedoStack] = useState([]);

    const [isPointerDown, setIsPointerDown] = useState(false);
    const [activeDraft, setActiveDraft] = useState(null);
    const [pendingText, setPendingText] = useState({ visible: false, x: 0, y: 0, value: "" });
    const [selectedTextIndex, setSelectedTextIndex] = useState(null);
    const [hoveredTextIndex, setHoveredTextIndex] = useState(null);
    const [canCloseTextSelectionWithEnter, setCanCloseTextSelectionWithEnter] = useState(false);
    const [textInteraction, setTextInteraction] = useState(null);

    useEffect(() => {
        if (!imageFile || !(imageFile instanceof Blob)) {
            setImageUrl(null);
            return;
        }
        // Create an object URL and draw the image; be careful with Strict Mode double-effects.
        const objectUrl = URL.createObjectURL(imageFile);
        setImageUrl(objectUrl);
        const img = new Image();
        img.onload = () => {
            const width = img.naturalWidth || img.width;
            const height = img.naturalHeight || img.height;
            setImageSize({ width, height });
            const baseCanvas = baseCanvasRef.current;
            const overlayCanvas = overlayCanvasRef.current;
            [baseCanvas, overlayCanvas].forEach((c) => {
                if (!c) return;
                c.width = width;
                c.height = height;
                const ctx = c.getContext("2d");
                if (ctx) ctx.imageSmoothingEnabled = true;
            });
            const ctx = baseCanvas.getContext("2d");
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);
        };
        img.onerror = () => {
            // Fallback for rare cases where object URL fails due to early revocation
            const reader = new FileReader();
            reader.onload = () => {
                setImageUrl(reader.result);
                const fimg = new Image();
                fimg.onload = () => {
                    const width = fimg.naturalWidth || fimg.width;
                    const height = fimg.naturalHeight || fimg.height;
                    setImageSize({ width, height });
                    const baseCanvas = baseCanvasRef.current;
                    const overlayCanvas = overlayCanvasRef.current;
                    [baseCanvas, overlayCanvas].forEach((c) => {
                        if (!c) return;
                        c.width = width;
                        c.height = height;
                        const ctx = c.getContext("2d");
                        if (ctx) ctx.imageSmoothingEnabled = true;
                    });
                    const ctx = baseCanvas.getContext("2d");
                    ctx.clearRect(0, 0, width, height);
                    ctx.drawImage(fimg, 0, 0, width, height);
                };
                fimg.src = reader.result;
            };
            reader.readAsDataURL(imageFile);
        };
        img.src = objectUrl;
        return () => {
            try {
                URL.revokeObjectURL(objectUrl);
            } catch { }
        };
    }, [imageFile]);

    const replay = (ctx, currentActions = actions, options = { includeText: true }) => {
        ctx.clearRect(0, 0, imageSize.width, imageSize.height);
        currentActions.forEach((a) => drawAction(ctx, a, false, options));
        if (activeDraft) {
            drawAction(ctx, activeDraft, true, options);
        }
    };

    const getTextBoxWidth = (action, ctx) => {
        if (action.boxWidth) return action.boxWidth;
        if (ctx) {
            ctx.save();
            ctx.font = `bold ${action.size}px Arial`;
            const measured = ctx.measureText(action.value || "").width;
            ctx.restore();
            return Math.max(120, measured + 16);
        }
        return Math.max(120, (action.value?.length || 1) * action.size * 0.65);
    };

    const getWrappedLines = (ctx, value, maxWidth) => {
        const text = (value || "").trim();
        if (!text) return [""];
        if (!maxWidth || !Number.isFinite(maxWidth)) return [text];
        const words = text.split(/\s+/);
        const lines = [];
        let current = words[0] || "";
        for (let i = 1; i < words.length; i += 1) {
            const candidate = `${current} ${words[i]}`;
            if (ctx.measureText(candidate).width <= maxWidth) {
                current = candidate;
            } else {
                lines.push(current);
                current = words[i];
            }
        }
        lines.push(current);
        return lines;
    };

    const drawAction = (ctx, a, isDraft = false, options = { includeText: true }) => {
        if (!a) return;
        if (!options.includeText && a.type === "text") return;
        switch (a.type) {
            case "pen": {
                ctx.strokeStyle = a.color;
                ctx.lineWidth = a.width;
                ctx.lineJoin = "round";
                ctx.lineCap = "round";
                ctx.beginPath();
                a.points.forEach((p, i) => {
                    if (i === 0) ctx.moveTo(p.x, p.y);
                    else ctx.lineTo(p.x, p.y);
                });
                ctx.stroke();
                break;
            }
            case "highlight": {
                ctx.save();
                ctx.globalAlpha = 0.35;
                ctx.fillStyle = a.color;
                const x = Math.min(a.start.x, a.end.x);
                const y = Math.min(a.start.y, a.end.y);
                const w = Math.abs(a.start.x - a.end.x);
                const h = Math.abs(a.start.y - a.end.y);
                ctx.fillRect(x, y, w, h);
                ctx.restore();
                break;
            }
            case "arrow": {
                const headlen = Math.max(8, a.width * 3);
                const dx = a.end.x - a.start.x;
                const dy = a.end.y - a.start.y;
                const angle = Math.atan2(dy, dx);
                ctx.strokeStyle = a.color;
                ctx.lineWidth = a.width;
                ctx.lineCap = "round";
                ctx.beginPath();
                ctx.moveTo(a.start.x, a.start.y);
                ctx.lineTo(a.end.x, a.end.y);
                ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(a.end.x, a.end.y);
                ctx.lineTo(
                    a.end.x - headlen * Math.cos(angle - Math.PI / 6),
                    a.end.y - headlen * Math.sin(angle - Math.PI / 6)
                );
                ctx.lineTo(
                    a.end.x - headlen * Math.cos(angle + Math.PI / 6),
                    a.end.y - headlen * Math.sin(angle + Math.PI / 6)
                );
                ctx.lineTo(a.end.x, a.end.y);
                ctx.fillStyle = a.color;
                ctx.fill();
                break;
            }
            case "text": {
                ctx.fillStyle = a.color;
                ctx.font = `bold ${a.size}px Arial`;
                ctx.textBaseline = "top";
                const boxWidth = getTextBoxWidth(a, ctx);
                const lines = getWrappedLines(ctx, a.value, boxWidth);
                const lineHeight = Math.round(a.size * 1.25);
                lines.forEach((line, index) => {
                    ctx.fillText(line, a.x, a.y + index * lineHeight);
                });
                break;
            }
            default:
                break;
        }
    };

    const onPointerDown = (e) => {
        // If text is visible, we're about to click somewhere.
        // We want to commit the current text, but avoid the subsequent blur event
        // from hiding the *new* text box we might be about to create.
        if (pendingText.visible) {
            ignoreBlurRef.current = true;
            commitText();
            // Reset ignore flag after a short delay so legitimate blurs (like clicking Save) still work
            setTimeout(() => {
                ignoreBlurRef.current = false;
            }, 200);
        }

        const point = getCanvasPoint(e);
        if (!point) return;
        const { x, y } = point;
        setRedoStack([]);
        if (tool === "pen") {
            setActiveDraft({
                type: "pen",
                color: strokeColor,
                width: strokeWidth,
                points: [{ x, y }],
            });
        } else if (tool === "highlight") {
            setActiveDraft({
                type: "highlight",
                color: highlightColor,
                start: { x, y },
                end: { x, y },
            });
        } else if (tool === "arrow") {
            setActiveDraft({
                type: "arrow",
                color: strokeColor,
                width: strokeWidth,
                start: { x, y },
                end: { x, y },
            });
        } else if (tool === "text") {
            setPendingText({ visible: true, x, y, value: "" });
            setSelectedTextIndex(null);
            setIsPointerDown(false);
            return;
        }
        setIsPointerDown(true);
    };

    const onCanvasClick = (e) => {
        if (tool !== "text" || pendingText.visible) return;
        const point = getCanvasPoint(e);
        if (!point) return;
        setPendingText({ visible: true, x: point.x, y: point.y, value: "" });
        setSelectedTextIndex(null);
    };

    const onPointerMove = (e) => {
        const canvas = overlayCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        if (!isPointerDown) {
            // Only need to refresh drafts when dragging
            return;
        }
        const { x, y } = getCanvasPoint(e);
        setActiveDraft((draft) => {
            if (!draft) return draft;
            if (draft.type === "pen") {
                const updated = { ...draft, points: [...draft.points, { x, y }] };
                ctx.clearRect(0, 0, imageSize.width, imageSize.height);
                replay(ctx, actions, { includeText: false });
                return updated;
            }
            if (draft.type === "highlight" || draft.type === "arrow") {
                const updated = { ...draft, end: { x, y } };
                ctx.clearRect(0, 0, imageSize.width, imageSize.height);
                replay(ctx, actions, { includeText: false });
                return updated;
            }
            return draft;
        });
        ctx.clearRect(0, 0, imageSize.width, imageSize.height);
        replay(ctx, actions, { includeText: false });
    };

    const onPointerUp = () => {
        if (!isPointerDown) return;
        setIsPointerDown(false);
        if (tool === "text") return;
        if (activeDraft) {
            setActions((prev) => [...prev, activeDraft]);
            setActiveDraft(null);
        }
        const ctx = overlayCanvasRef.current.getContext("2d");
        ctx.clearRect(0, 0, imageSize.width, imageSize.height);
        replay(ctx, actions, { includeText: false });
    };

    const getCanvasPointFromClient = (clientX, clientY) => {
        const canvas = overlayCanvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const x = ((clientX - rect.left) / rect.width) * imageSize.width;
        const y = ((clientY - rect.top) / rect.height) * imageSize.height;
        return { x, y };
    };

    const getCanvasPoint = (e) => {
        return getCanvasPointFromClient(e.clientX, e.clientY);
    };

    const commitText = () => {
        if (!pendingText.visible || !pendingText.value.trim()) {
            setPendingText({ visible: false, x: 0, y: 0, value: "" });
            return;
        }
        const action = {
            type: "text",
            color: strokeColor,
            size: Math.max(14, strokeWidth * 5),
            x: pendingText.x,
            y: pendingText.y,
            value: pendingText.value.trim(),
            boxWidth: Math.max(
                140,
                Math.min(imageSize.width * 0.6, pendingText.value.trim().length * Math.max(14, strokeWidth * 5) * 0.7)
            ),
        };
        const newActions = [...actions, action];
        setActions(newActions);
        setPendingText({ visible: false, x: 0, y: 0, value: "" });
        setCanCloseTextSelectionWithEnter(false);
        const ctx = overlayCanvasRef.current.getContext("2d");
        ctx.clearRect(0, 0, imageSize.width, imageSize.height);
        replay(ctx, newActions, { includeText: false });
    };

    const handleUndo = () => {
        setActions((prev) => {
            if (prev.length === 0) return prev;
            const next = [...prev];
            const popped = next.pop();
            // setRedoStack((r) => [...r, popped]); // Redo removed
            const ctx = overlayCanvasRef.current.getContext("2d");
            ctx.clearRect(0, 0, imageSize.width, imageSize.height);
            replay(ctx, next, { includeText: false });
            return next;
        });
    };

    const handleRedo = () => {
        setRedoStack((prev) => {
            if (prev.length === 0) return prev;
            const next = [...prev];
            const action = next.pop();
            setActions((a) => {
                const combined = [...a, action];
                const ctx = overlayCanvasRef.current.getContext("2d");
                ctx.clearRect(0, 0, imageSize.width, imageSize.height);
                replay(ctx, combined, { includeText: false });
                return combined;
            });
            return next;
        });
    };

    const handleSizeChange = (value) => {
        const nextStrokeWidth = parseInt(value, 10);
        setStrokeWidth(nextStrokeWidth);
        const textIndexToUpdate =
            selectedTextIndex !== null ? selectedTextIndex : hoveredTextIndex;
        if (textIndexToUpdate === null) return;
        setSelectedTextIndex(textIndexToUpdate);
        setCanCloseTextSelectionWithEnter(true);
        setActions((prev) =>
            prev.map((action, index) => {
                if (index !== textIndexToUpdate || action.type !== "text") return action;
                return { ...action, size: Math.max(14, nextStrokeWidth * 5) };
            })
        );
    };

    const handleSave = async (mode) => {
        const exportCanvas = document.createElement("canvas");
        exportCanvas.width = imageSize.width;
        exportCanvas.height = imageSize.height;
        const ctx = exportCanvas.getContext("2d");
        ctx.drawImage(baseCanvasRef.current, 0, 0);
        // Draw all actions (final render)
        actions.forEach((a) => drawAction(ctx, a));
        if (activeDraft) drawAction(ctx, activeDraft);
        await new Promise((resolve) => {
            exportCanvas.toBlob(
                (blob) => {
                    onSave(blob, { replaceOriginal: mode === "replace" });
                    resolve();
                },
                "image/png",
                0.95
            );
        });
    };

    useEffect(() => {
        if (pendingText.visible && textInputRef.current) {
            textInputRef.current.focus();
        }
    }, [pendingText.visible]);

    useEffect(() => {
        if (!textInteraction) return undefined;
        const onMouseMove = (e) => {
            const point = getCanvasPointFromClient(e.clientX, e.clientY);
            if (!point) return;
            setActions((prev) =>
                prev.map((action, index) => {
                    if (index !== textInteraction.actionIndex || action.type !== "text") return action;
                    if (textInteraction.type === "drag") {
                        const nextX = Math.max(
                            0,
                            Math.min(
                                imageSize.width - 40,
                                textInteraction.initialX + (point.x - textInteraction.startX)
                            )
                        );
                        const nextY = Math.max(
                            0,
                            Math.min(
                                imageSize.height - action.size * 1.5,
                                textInteraction.initialY + (point.y - textInteraction.startY)
                            )
                        );
                        return { ...action, x: nextX, y: nextY };
                    }
                    if (textInteraction.type === "resize") {
                        return {
                            ...action,
                            boxWidth: Math.max(
                                80,
                                Math.min(
                                    imageSize.width - action.x,
                                    textInteraction.initialWidth + (point.x - textInteraction.startX)
                                )
                            ),
                        };
                    }
                    return action;
                })
            );
        };
        const onMouseUp = () => setTextInteraction(null);
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        };
    }, [textInteraction, imageSize.width, imageSize.height]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key !== "Enter" || pendingText.visible) return;
            if (!canCloseTextSelectionWithEnter) return;
            if (selectedTextIndex === null && hoveredTextIndex === null) return;
            setSelectedTextIndex(null);
            setHoveredTextIndex(null);
            setTextInteraction(null);
            setCanCloseTextSelectionWithEnter(false);
        };
        window.addEventListener("keydown", onKeyDown);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [selectedTextIndex, hoveredTextIndex, pendingText.visible, canCloseTextSelectionWithEnter]);

    // Scale to fit viewport while keeping native resolution on canvas
    const computeStyleSize = () => {
        const maxW = Math.min(window.innerWidth - 48, 1200);
        const maxH = Math.min(window.innerHeight - 200, 800);
        const { width, height } = imageSize;
        if (!width || !height) {
            const fallbackW = Math.min(maxW, 800);
            return { width: fallbackW, height: 320 };
        }
        const ratio = Math.min(maxW / width, maxH / height, 1);
        return { width: width * ratio, height: height * ratio };
    };

    const styleSize = computeStyleSize();
    const displayScale = imageSize.width ? styleSize.width / imageSize.width : 1;
    const pendingInputWidth = Math.max(180, Math.min(360, 220 * displayScale));
    const pendingInputLeft = Math.max(
        0,
        Math.min(styleSize.width - pendingInputWidth, pendingText.x * displayScale)
    );
    const pendingInputTop = Math.max(
        0,
        Math.min(styleSize.height - 42, pendingText.y * displayScale)
    );

    const startTextDrag = (e, actionIndex) => {
        e.preventDefault();
        e.stopPropagation();
        const action = actions[actionIndex];
        const point = getCanvasPointFromClient(e.clientX, e.clientY);
        if (!point || !action || action.type !== "text") return;
        setSelectedTextIndex(actionIndex);
        setCanCloseTextSelectionWithEnter(false);
        setTextInteraction({
            type: "drag",
            actionIndex,
            startX: point.x,
            startY: point.y,
            initialX: action.x,
            initialY: action.y,
        });
    };

    const startTextResize = (e, actionIndex) => {
        e.preventDefault();
        e.stopPropagation();
        const action = actions[actionIndex];
        const point = getCanvasPointFromClient(e.clientX, e.clientY);
        if (!point || !action || action.type !== "text") return;
        setSelectedTextIndex(actionIndex);
        setCanCloseTextSelectionWithEnter(false);
        setTextInteraction({
            type: "resize",
            actionIndex,
            startX: point.x,
            initialWidth: getTextBoxWidth(action),
        });
    };

    const deleteTextAction = (actionIndex) => {
        setActions((prev) => prev.filter((_, index) => index !== actionIndex));
        setSelectedTextIndex(null);
        setHoveredTextIndex((prev) => (prev === actionIndex ? null : prev));
        setCanCloseTextSelectionWithEnter(false);
    };

    return (
        <div
            ref={containerRef}
            className="fixed inset-0 z-[1000] bg-black/60 flex items-center justify-center"
        >
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-[90vw]">
                <div className="flex items-center gap-2 p-3 border-b">
                    <span className="font-medium mr-2 truncate">
                        Annotate: {imageFile?.name}
                    </span>
                    <div className="flex items-center gap-1">
                        <button
                            className={`px-2 py-1 rounded ${tool === "pen" ? "bg-gray-200" : ""}`}
                            onClick={() => setTool("pen")}
                            title="Pen"
                        >
                            <Pencil size={18} />
                        </button>
                        <button
                            className={`px-2 py-1 rounded ${tool === "highlight" ? "bg-gray-200" : ""}`}
                            onClick={() => setTool("highlight")}
                            title="Highlight"
                        >
                            <Highlighter size={18} />
                        </button>
                        <button
                            className={`px-2 py-1 rounded ${tool === "arrow" ? "bg-gray-200" : ""}`}
                            onClick={() => setTool("arrow")}
                            title="Arrow"
                        >
                            <ArrowRight size={18} />
                        </button>
                        <button
                            className={`px-2 py-1 rounded ${tool === "text" ? "bg-gray-200" : ""}`}
                            onClick={() => setTool("text")}
                            title="Text"
                        >
                            <Type size={18} />
                        </button>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                        <label className="text-xs text-gray-500">Color</label>
                        <input
                            type="color"
                            value={strokeColor}
                            onChange={(e) => setStrokeColor(e.target.value)}
                            className="w-6 h-6"
                        />
                        <label className="text-xs text-gray-500 ml-2">Size</label>
                        <input
                            type="range"
                            min="2"
                            max="16"
                            value={strokeWidth}
                            onChange={(e) => handleSizeChange(e.target.value)}
                        />
                        <label className="text-xs text-gray-500 ml-2">Highlight</label>
                        <input
                            type="color"
                            value={highlightColor}
                            onChange={(e) => setHighlightColor(e.target.value)}
                            className="w-6 h-6"
                        />
                    </div>
                    <div className="flex-1" />
                    <div className="flex items-center gap-2">
                        <button
                            className="px-2 py-1 rounded hover:bg-gray-100"
                            onClick={handleUndo}
                            title="Undo"
                        >
                            <Undo size={18} />
                        </button>
                        <button
                            className="px-2 py-1 rounded hover:bg-gray-100"
                            onClick={onClose}
                            title="Close"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                <div className="p-3 flex items-center justify-center">
                    <div
                        className="relative min-h-[200px]"
                        style={{ width: styleSize.width, height: styleSize.height }}
                    >
                        <canvas
                            ref={baseCanvasRef}
                            className="absolute inset-0 w-full h-full"
                            style={{ touchAction: "none" }}
                        />
                        <canvas
                            ref={overlayCanvasRef}
                            className="absolute inset-0 w-full h-full cursor-crosshair"
                            onMouseDown={onPointerDown}
                            onClick={onCanvasClick}
                            onMouseMove={onPointerMove}
                            onMouseUp={onPointerUp}
                            onMouseLeave={onPointerUp}
                            style={{ touchAction: "none" }}
                        />
                        {actions.map((action, index) => {
                            if (action.type !== "text") return null;
                            const isSelected = selectedTextIndex === index;
                            const boxWidth = getTextBoxWidth(action);
                            return (
                                <div
                                    key={`text-action-${index}`}
                                    className="absolute group"
                                    onMouseDown={(e) => startTextDrag(e, index)}
                                    onMouseEnter={() => setHoveredTextIndex(index)}
                                    onMouseLeave={() =>
                                        setHoveredTextIndex((prev) => (prev === index ? null : prev))
                                    }
                                    style={{
                                        left: action.x * displayScale,
                                        top: action.y * displayScale,
                                        width: Math.max(80, boxWidth * displayScale),
                                        fontSize: Math.max(12, action.size * displayScale),
                                        color: action.color,
                                        fontWeight: 700,
                                        lineHeight: 1.25,
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        cursor: "move",
                                        border: isSelected ? "1px dashed #4f46e5" : "1px dashed transparent",
                                        background: isSelected ? "rgba(255,255,255,0.75)" : "transparent",
                                        borderRadius: 4,
                                        padding: "2px 4px",
                                        userSelect: "none",
                                    }}
                                >
                                    {action.value}
                                    <button
                                        type="button"
                                        className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white text-xs hidden group-hover:flex items-center justify-center"
                                        onMouseDown={(e) => {
                                            e.stopPropagation();
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deleteTextAction(index);
                                        }}
                                        title="Delete text"
                                    >
                                        ×
                                    </button>
                                    <div
                                        className="absolute top-0 -right-1 h-full w-2 cursor-ew-resize"
                                        onMouseDown={(e) => startTextResize(e, index)}
                                        title="Resize text width"
                                    />
                                </div>
                            );
                        })}
                        {pendingText.visible && (
                            <input
                                ref={textInputRef}
                                value={pendingText.value}
                                onChange={(e) =>
                                    setPendingText((p) => ({ ...p, value: e.target.value }))
                                }
                                onBlur={() => {
                                    if (!ignoreBlurRef.current) commitText();
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") commitText();
                                    if (e.key === "Escape")
                                        setPendingText({ visible: false, x: 0, y: 0, value: "" });
                                }}
                                className="absolute border rounded px-2 py-1 text-sm shadow placeholder:text-white/70"
                                style={{
                                    left: pendingInputLeft,
                                    top: pendingInputTop,
                                    transform: "translate(-0%, -0%)",
                                    width: pendingInputWidth,
                                    zIndex: 60,
                                    backgroundColor: "#111111",
                                    color: "#ffffff",
                                    border: "1px solid #ffffff",
                                    boxShadow: "0 0 0 2px rgba(0,0,0,0.35), 0 4px 14px rgba(0,0,0,0.45)",
                                }}
                                placeholder="Input text here"
                            />
                        )}
                    </div>
                </div>

                <div className="p-3 border-t flex items-center justify-end gap-2">
                    <button
                        onClick={() => handleSave("new")}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded bg-gray-100 hover:bg-gray-200"
                        title="Save as new image"
                    >
                        <FilePlus2 size={18} />
                        Save as New
                    </button>
                    <button
                        onClick={() => handleSave("replace")}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                        title="Replace original attachment"
                    >
                        <Save size={18} />
                        Save & Replace
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImageAnnotatorModal;


